/**
 * HeaderControls — compact language + theme toggles that sit in the top-right
 * of both the dashboard shell and the public landing page.
 *
 *  ┌────────────────────────────────────────┐
 *  │  NL EN FR   [☀︎ ☾]                    │
 *  └────────────────────────────────────────┘
 *
 *  - Language switcher: pill row (NL/EN/FR). Reads current locale from i18next.
 *    On dashboard it also PATCHes /branding to persist the user's choice; on the
 *    public landing it only flips the client locale (no auth call).
 *  - Theme toggle: reuses existing `<ThemeToggle />` component (sun/moon slider).
 *  - Uses a subtle glass-morphism style so it works on both dark and light themes.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { setLocale } from "@/lib/i18n";
import { api } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";

export default function HeaderControls({ variant = "app", refreshWorkspace, className = "" }) {
    const { i18n } = useTranslation();
    const active = i18n.language?.slice(0, 2) || "nl";

    const switchLang = async (lng) => {
        setLocale(lng);
        i18n.changeLanguage(lng);
        // Only persist when running inside the authenticated dashboard shell.
        if (variant === "app") {
            try {
                await api.patch("/branding", { locale: lng });
                refreshWorkspace?.();
            } catch (e) {
                /* non-blocking — locale still flips in the UI */
            }
        }
    };

    return (
        <div
            data-testid="header-controls"
            className={`inline-flex items-center gap-1 rounded-full border border-white/10 bg-[var(--jr-surface)]/85 backdrop-blur px-1 py-1 ${className}`}
        >
            {["nl", "en", "fr"].map((lng) => (
                <button
                    key={lng}
                    onClick={() => switchLang(lng)}
                    data-testid={`header-lang-${lng}`}
                    className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-full transition-colors ${
                        active === lng
                            ? "bg-[var(--jr-brand)] text-white"
                            : "text-[var(--jr-text-2)] hover:bg-white/5"
                    }`}
                >
                    {lng}
                </button>
            ))}
            <div className="w-px h-4 bg-white/10 mx-1" aria-hidden />
            <ThemeToggle />
        </div>
    );
}
